// ***************************************
// ComponentBase
//
// This component contains the basic functionality of all components
//
// Copyright 2015 Tingly Games B.V.
// Last Changed: 27-9-2015
// ***************************************
import { tbGame } from "../../engine/tbGame";
import { tgfEffects } from "../../framework/tgfEffects";
import { tgfFunctions } from "../tgfFunctions";
import { tgfSettings } from "../tgfSettings";
import { tbVector } from "../../engine/tbVector";
tgfSettings.componentDefaults.base = {
    tags: [],
    // Position and size
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    // Transformation
    xScale: 1,
    yScale: 1,
    angle: 0,
    alpha: 1,
    // Effects
    effects: null,
    pauseEffectsOnDeactivate: false,
    repeatEffect: 99999999,
    effectWaitSteps: 2,
    onEffectStart: null,
    onEffectEnd: null,
    onSubEffectStart: null,
    onSubEffectEnd: null,
    baseContainer: null,
    pivot: { x: 0, y: 0 }
};
var ComponentBase = /** @class */ (function () {
    function ComponentBase(properties) {
        // Initialize properties
        this.initialize();
        // Set user properties
        if (properties !== undefined) {
            this.setProperties(properties);
        }
    }
    // Meant to be overriden
    ComponentBase.prototype.doShow = function () { };
    ComponentBase.prototype.doHide = function () { };
    ComponentBase.prototype.doFree = function () { };
    ComponentBase.prototype.doPrepare = function () { };
    ComponentBase.prototype.doActivate = function () { };
    ComponentBase.prototype.doDeactivate = function () { };
    ComponentBase.prototype.doDraw = function () { };
    ComponentBase.prototype.doUpdateVisuals = function (timePassed) { };
    ComponentBase.prototype.doEndStep = function (timePassed) { };
    ComponentBase.prototype.doSpecial = function (id, args) { };
    ComponentBase.prototype.doTouchDown = function (finger, x, y) { return false; };
    ComponentBase.prototype.doTouchUp = function (finger, x, y) { return false; };
    ComponentBase.prototype.doImmediateTouchDown = function (finger, x, y) { return false; };
    ComponentBase.prototype.doImmediateTouchUp = function (finger, x, y) { return false; };
    ComponentBase.prototype.doMouseWheel = function (delta, x, y) { return false; };
    ComponentBase.prototype.doKeyDown = function (keyCode) { };
    // ***************************************
    // General methods
    // ***************************************
    ComponentBase.prototype.addChild = function (child) {
        this.baseContainer.addChild(child);
    };
    // Initialize the properties of the component
    ComponentBase.prototype.initialize = function (properties) {
        this.setProperties(tgfSettings.componentDefaults.base); // The base properties must always be included first
        this.setProperties(properties);
        this.parent = null; // The parent component or dialog
        this.kind = "component"; // The kind (must be "component")
        this.isPrepared = false; // Whether prepared (true, false)
        this.isVisible = false; // Whether visible (true, false)
        this.isActive = false; // Whether active (true, false)
        this._components = []; // The child components
    };
    /**
     * Changes the global position of the component.
     * The position of the component will be converted to global space on preparing based on it's parent, so you get different result when using this before prepare and after prepare.
     */
    ComponentBase.prototype.setPosition = function (x, y) {
        var hasXValue = x !== undefined && x !== null;
        var hasYValue = y !== undefined && y !== null;
        if (hasXValue) {
            this.x = x - this.pivot.x * this.width;
        }
        if (hasYValue) {
            this.y = y - this.pivot.y * this.height;
        }
    };
    /**
     * Changes the position of the component but convert it to global space based on the parent's position.
     * If this is called before the component is prepared, it will convert the position to global space again on preparing which can mess up the position.
     */
    ComponentBase.prototype.setLocalPosition = function (x, y) {
        var hasXValue = x !== undefined && x !== null;
        var hasYValue = y !== undefined && y !== null;
        if (!hasXValue && !hasYValue) {
            return;
        }
        // Add parent position to it so it becames a global position
        // Since the x and y are global positions, we only need to add our parents position instead of all the upper parents.
        if (hasXValue) {
            x += this.parent.x;
        }
        if (hasYValue) {
            y += this.parent.y;
        }
        this.setPosition(x, y);
    };
    ComponentBase.prototype.containerRelativeX = function () {
        return this.x + this.parent.containerRelativeX();
    };
    ComponentBase.prototype.containerRelativeY = function () {
        return this.y + this.parent.containerRelativeY();
    };
    ComponentBase.prototype.getUpperParent = function () {
        var parent = this.parent;
        while (parent) {
            if (parent.parent) {
                parent = parent.parent;
            }
        }
        return parent;
    };
    ComponentBase.prototype.setAngle = function (angle) {
        this.angle = angle;
    };
    ComponentBase.prototype.setAlpha = function (alpha) {
        this.alpha = alpha;
    };
    ComponentBase.prototype.setScale = function (xScale, yScale) {
        this.xScale = xScale;
        this.yScale = yScale;
    };
    // Set a number of properties of the component
    ComponentBase.prototype.setProperties = function (properties) {
        var property;
        if (typeof properties !== "object") {
            return;
        }
        for (property in properties) {
            if (properties.hasOwnProperty(property)) {
                this[property] = tgfFunctions.copyValue(properties[property]);
            }
        }
    };
    // Set the parent of the component to a component or dialog
    ComponentBase.prototype.setParent = function (parent) {
        if (this.parent !== null) {
            parent.removeComponent(this);
        }
        parent.addComponent(this);
    };
    // Returns the dialog the component belongs to
    ComponentBase.prototype.getDialog = function () {
        var result = this.parent;
        while (result !== null && result.kind !== "dialog") {
            result = result.parent;
        }
        return result;
    };
    // Transform the screen (x,y)-coordinate to the component x-coordinate
    ComponentBase.prototype.screenToComponentX = function (screenX, screenY) {
        var x, y, rot = this.angle * Math.PI / 180;
        // Find position with respect to parent
        if (this.parent !== null) {
            x = this.parent.screenToComponentX(screenX, screenY);
            y = this.parent.screenToComponentY(screenX, screenY);
        }
        else {
            x = screenX;
            y = screenY;
        }
        // Compute position after the transformation
        x = x - this.x;
        if (this.angle !== 0) {
            x = x * Math.cos(rot) - y * Math.sin(rot);
        }
        if (this.xScale !== 0 && this.xScale !== 1) {
            x /= this.xScale;
        }
        return x;
    };
    // Transform the screen (x,y)-coordinate to the component y-coordinate
    ComponentBase.prototype.screenToComponentY = function (screenX, screenY) {
        var x, y, rot = this.angle * Math.PI / 180;
        // Find position with respect to parent
        if (this.parent !== null) {
            x = this.parent.screenToComponentX(screenX, screenY);
            y = this.parent.screenToComponentY(screenX, screenY);
        }
        else {
            x = screenX;
            y = screenY;
        }
        // Compute position after the transformation
        y = y - this.y;
        if (this.angle !== 0) {
            y = x * Math.sin(rot) + y * Math.cos(rot);
        }
        if (this.yScale !== 0 && this.yScale !== 1) {
            y /= this.yScale;
        }
        return y;
    };
    // Transform the screen (x,y)-coordinate to a component-space vector
    ComponentBase.prototype.screenToComponentVector = function (screenX, screenY) {
        return new tbVector(this.screenToComponentX(screenX, screenY), this.screenToComponentY(screenX, screenY));
    };
    // Transform the component (x,y)-coordinate to the screen x-coordinate
    ComponentBase.prototype.componentToScreenX = function (componentX, componentY) {
        var x = componentX, y = componentY, rot = this.angle * Math.PI / 180;
        x *= this.xScale === 0 ? 1 : this.xScale;
        y *= this.yScale === 0 ? 1 : this.yScale;
        if (this.angle !== 0) {
            x = x * Math.cos(rot) - y * Math.sin(rot);
            y = x * Math.sin(rot) + y * Math.cos(rot);
        }
        // Find position with respect to parent if it exists
        if (this.parent !== null) {
            return this.parent.componentToScreenX(x + this.x, y + this.y);
        }
        return x + this.x;
    };
    // Transform the component (x,y)-coordinate to the screen y-coordinate
    ComponentBase.prototype.componentToScreenY = function (componentX, componentY) {
        var x = componentX, y = componentY, rot = this.angle * Math.PI / 180;
        x *= this.xScale === 0 ? 1 : this.xScale;
        y *= this.yScale === 0 ? 1 : this.yScale;
        if (this.angle !== 0) {
            x = x * Math.cos(rot) - y * Math.sin(rot);
            y = x * Math.sin(rot) + y * Math.cos(rot);
        }
        // Find position with respect to parent if it exists
        if (this.parent !== null) {
            return this.parent.componentToScreenY(x + this.x, y + this.y);
        }
        return y + this.y;
    };
    // Transform the component (x,y)-coordinate to a tbVector representing the screen position
    ComponentBase.prototype.componentToScreenVector = function (componentX, componentY) {
        return new tbVector(this.componentToScreenX(componentX, componentY), this.componentToScreenY(componentX, componentY));
    };
    // Returns the x-position of the finger with respect to the component
    ComponentBase.prototype.touchX = function (finger) {
        return this.screenToComponentX(tbGame.touchX(finger), tbGame.touchY(finger));
    };
    // Returns the y-position of the finger with respect to the component
    ComponentBase.prototype.touchY = function (finger) {
        return this.screenToComponentY(tbGame.touchX(finger), tbGame.touchY(finger));
    };
    // Returns the width of the component. Must be overriden if it is not the width property
    ComponentBase.prototype.getWidth = function () {
        return this.width === undefined ? 0 : this.width;
    };
    // Returns the height of the component. Must be overriden if it is not the height property
    ComponentBase.prototype.getHeight = function () {
        return this.height === undefined ? 0 : this.height;
    };
    // Returns the left position of the component (in its own coordinate system). Must be overriden if it is not centered
    ComponentBase.prototype.getLeft = function () {
        return -this.getWidth() / 2;
    };
    // Returns the top position of the component (in its own coordinate system). Must be overriden if it is not centered
    ComponentBase.prototype.getTop = function () {
        return -this.getHeight() / 2;
    };
    // ***************************************
    // Dealing with tags
    // ***************************************
    // Clears the tags
    ComponentBase.prototype.clearTags = function () {
        this.tags = [];
    };
    // Returns (a copy of) the tags
    ComponentBase.prototype.getTags = function () {
        return this.tags.slice(0);
    };
    // Add tags
    ComponentBase.prototype.addTags = function (tags) {
        var t;
        tags = [].concat(tags); // Such that it also works for a single value
        for (t = 0; t < tags.length; t += 1) {
            if (this.tags.indexOf(tags[t]) < 0) {
                this.tags.push(tags[t]);
            }
        }
    };
    // Remove tags
    ComponentBase.prototype.removeTags = function (tags) {
        var t, ind;
        tags = [].concat(tags); // Such that it also works for a single value
        for (t = 0; t < tags.length; t += 1) {
            ind = this.tags.indexOf(tags[t]);
            if (ind >= 0) {
                this.tags.splice(ind, 1);
            }
        }
    };
    // Returns whether the tags contain one or all of the given values
    ComponentBase.prototype.containsTags = function (tags, all) {
        var t, ind;
        if (all === undefined) {
            all = false;
        }
        tags = [].concat(tags); // Such that it also works for a single value
        for (t = 0; t < tags.length; t += 1) {
            ind = this.tags.indexOf(tags[t]);
            if (ind >= 0 && !all) {
                return true;
            }
            if (ind < 0 && all) {
                return false;
            }
        }
        return true;
    };
    // ***************************************
    // Dealing with child components
    // ***************************************
    // Add a child component
    ComponentBase.prototype.addComponent = function (component) {
        component.parent = this;
        this._components.push(component);
    };
    // Insert a child component
    ComponentBase.prototype.insertComponent = function (index, component) {
        component.parent = this;
        if (index < 0) {
            index = 0;
        }
        if (index > this._components.length) {
            index = this._components.length;
        }
        this._components.splice(index, 0, component);
    };
    // Returns the component with the given name
    ComponentBase.prototype.findComponent = function (name, recursive) {
        var i, result = null;
        if (recursive === undefined) {
            recursive = false;
        }
        for (i = 0; i < this._components.length; i += 1) {
            if (this._components[i].name === name) {
                result = this._components[i];
            }
            if (recursive) {
                result = this._components[i].findComponent(name, recursive);
                if (result !== null) {
                    return result;
                }
            }
        }
        return result;
    };
    // Removes all components
    ComponentBase.prototype.removeAllComponents = function () {
        this._components = [];
    };
    // Removes the component
    ComponentBase.prototype.removeComponent = function (component, recursive) {
        var i;
        if (recursive === undefined) {
            recursive = false;
        }
        for (i = this._components.length - 1; i >= 0; i -= 1) {
            if (recursive) {
                this._components[i].removeComponent(component, recursive);
            }
            if (this._components[i] === component) {
                this._components.splice(i, 1);
            }
        }
    };
    // Removes the component with the given name
    ComponentBase.prototype.removeComponentByName = function (name, recursive) {
        var i;
        if (recursive === undefined) {
            recursive = false;
        }
        for (i = this._components.length - 1; i >= 0; i -= 1) {
            if (recursive) {
                this._components[i].removeComponent(name, recursive);
            }
            if (this._components[i].name === name) {
                this._components.splice(i, 1);
            }
        }
    };
    // Returns the components whose tags contain one or all of the values
    ComponentBase.prototype.findComponentsByTags = function (tags, all, recursive) {
        var result = [];
        var result2;
        if (all === undefined) {
            all = false;
        }
        if (recursive === undefined) {
            recursive = false;
        }
        for (var i = 0; i < this._components.length; i += 1) {
            if (this._components[i].containsTags(tags, all)) {
                result.push(this._components[i]);
            }
            if (recursive) {
                result2 = this._components[i].findComponentsByTags(tags, all, recursive);
                result = result.concat(result2);
            }
        }
        return result;
    };
    // Removes the components whose tags contain one or all of the values
    ComponentBase.prototype.removeComponentsByTags = function (tags, all, recursive) {
        var i;
        if (all === undefined) {
            all = false;
        }
        if (recursive === undefined) {
            recursive = false;
        }
        for (i = this._components.length - 1; i >= 0; i -= 1) {
            if (recursive) {
                this._components[i].removeComponentsByTags(tags, all, recursive);
            }
            if (this._components[i].containsTags(tags, all)) {
                this._components.splice(i, 1);
            }
        }
    };
    // ***************************************
    // External methods related to effects
    // ***************************************
    // Restarts the effect (to be called after making changes)
    ComponentBase.prototype.restartEffect = function () {
        this._startEffects();
    };
    // Returns the index of the current sub-effect
    ComponentBase.prototype.getSubEffect = function () {
        var i, len = 0;
        if (this.effects === null) {
            return -1;
        }
        if (Object.prototype.toString.call(this.effects) === "[object Array]" && this.effects.length === 0) {
            return -1;
        }
        if (this.effectRepeat <= 0) {
            return -1;
        }
        if (Object.prototype.toString.call(this.effects) !== "[object Array]") {
            return 0;
        }
        for (i = 0; i < this.effects.length; i += 1) {
            len += this._getEffectDuration(this.effects[i], false);
            if (this.effectTime < len) {
                return i;
            }
        }
        return 0;
    };
    // Move to the next sub-effect
    ComponentBase.prototype.nextSubEffect = function () {
        this.startSubEffect(this.getSubEffect() + 1);
    };
    // Move to the previous sub-effect
    ComponentBase.prototype.previousSubEffect = function () {
        this.startSubEffect(this.getSubEffect() - 1);
    };
    // Repeats the current sub-effect
    ComponentBase.prototype.repeatSubEffect = function () {
        this.startSubEffect(this.getSubEffect());
    };
    // Starts the indicated sub-effect
    ComponentBase.prototype.startSubEffect = function (index) {
        var i;
        // Easy case
        this.effectTime = 0;
        if (Object.prototype.toString.call(this.effects) !== "[object Array]") {
            return;
        }
        // Correct the index
        if (index === undefined) {
            index = 0;
        }
        if (index < 0) {
            index = 0;
        }
        if (index >= this.effects.length) {
            index = this.effects.length - 1;
        }
        for (i = 0; i < index; i += 1) {
            this.effectTime += this._getEffectDuration(this.effects[i], false);
        }
    };
    // ***************************************
    // Internal methods related to effects
    // ***************************************
    // Returns the duration of the (sub-effect)
    ComponentBase.prototype._getEffectDuration = function (effect, sequential) {
        var i, len, result = 0;
        if (sequential === undefined) {
            sequential = true;
        }
        // Easy cases
        if (effect === null) {
            return 0;
        }
        if (Object.prototype.toString.call(effect) === "[object Array]" && effect.length === 0) {
            return 0;
        }
        if (Object.prototype.toString.call(effect) !== "[object Array]") {
            return effect.duration;
        }
        // Otherwise, compute it from the array elements
        for (i = 0; i < effect.length; i += 1) {
            len = this._getEffectDuration(effect[i], !sequential);
            if (sequential) {
                result += len;
            }
            else {
                result = Math.max(result, len);
            }
        }
        return result;
    };
    // Starts the effects sequence
    ComponentBase.prototype._startEffects = function () {
        if (this.effects === null) {
            return;
        }
        if (Object.prototype.toString.call(this.effects) === "[object Array]" && this.effects.length === 0) {
            return;
        }
        this.effectDuration = this._getEffectDuration(this.effects, true); // The combined duration of the effects
        this.effectTime = 0; // The progress in seconds in the current effect
        this.effectWait = this.effectWaitSteps; // Number of steps to wait
        this.effectRepeat = this.repeatEffect; // How often the effects must still be performed
        this.effectCurrent = []; // List of current effects
    };
    // Updates the effect
    ComponentBase.prototype._updateEffects = function (timePassed) {
        if (this.effects === null) {
            return;
        }
        if (Object.prototype.toString.call(this.effects) === "[object Array]" && this.effects.length === 0) {
            return;
        }
        if (this.effectRepeat <= 0) {
            return;
        }
        // Wait before updating the effect timer (to avoid initial hick-ups)
        if (this.effectWait > 0) {
            this.effectWait -= 1;
        }
        else {
            this.effectTime += timePassed / 1000;
        }
        // Check whether an effect sequence is finished
        if (this.effectTime >= this.effectDuration) {
            if (this.onEffectEnd) {
                this.onEffectEnd(this);
            }
            this.effectRepeat -= 1;
            if (this.effectRepeat <= 0) {
                return;
            }
            this.effectTime = this.effectTime - this.effectDuration;
            if (this.onEffectStart) {
                this.onEffectStart(this);
            }
        }
    };
    // Returns an array the effects that must be performed at moment time
    ComponentBase.prototype._findEffects = function (effect, time, sequential, result) {
        var i, len, inc;
        // Easy cases
        if (effect === null) {
            return;
        }
        if (Object.prototype.toString.call(effect) === "[object Array]" && effect.length === 0) {
            return;
        }
        if (Object.prototype.toString.call(effect) !== "[object Array]") {
            if (time < effect.duration) {
                result.push({ effect: effect, time: time });
            }
            return;
        }
        // The difficult cases
        if (sequential) {
            len = 0;
            for (i = 0; i < effect.length; i += 1) {
                inc = this._getEffectDuration(effect[i], !sequential);
                if (len + inc > time) {
                    this._findEffects(effect[i], time - len, !sequential, result);
                    return;
                }
                len += inc;
            }
        }
        else {
            for (i = 0; i < effect.length; i += 1) {
                this._findEffects(effect[i], time, !sequential, result);
            }
        }
    };
    // Perform the effects
    ComponentBase.prototype._performEffects = function () {
        var effectNew = [];
        var ef, i, j, found;
        if (this.effects === null) {
            return;
        }
        if (Object.prototype.toString.call(this.effects) === "[object Array]" && this.effects.length === 0) {
            return;
        }
        if (this.effectRepeat <= 0) {
            return;
        }
        // Find the new set of effect
        this._findEffects(this.effects, this.effectTime, true, effectNew);
        // Handle the sub-effect that must are stopped
        for (j = 0; j < this.effectCurrent.length; j += 1) {
            ef = this.effectCurrent[j];
            found = false;
            for (i = 0; i < effectNew.length; i += 1) {
                if (effectNew[i].effect === ef.effect) {
                    found = true;
                    break;
                }
            }
            if (!found) {
                // Perform the end of the effect to make sure the transformation is final
                if (typeof (ef.effect.effect) === "function") {
                    ef.effect.effect(this, ef.effect, ef.effect.duration);
                }
                else {
                    tgfEffects.effect(ef.effect.effect)(this, ef.effect, ef.effect.duration);
                }
                // Call the call-back
                if (this.onSubEffectEnd) {
                    this.onSubEffectEnd(this, ef.effect);
                }
            }
        }
        // Handle the sub-effect that must are started
        for (j = 0; j < effectNew.length; j += 1) {
            ef = effectNew[j];
            found = false;
            for (i = 0; i < this.effectCurrent.length; i += 1) {
                if (this.effectCurrent[i].effect === ef.effect) {
                    found = true;
                    break;
                }
            }
            if (!found) {
                // Call the call-back
                if (this.onSubEffectStart) {
                    this.onSubEffectStart(this, ef.effect);
                }
                // Perform the start of the effect to make sure the transformation is initialized
                if (typeof (ef.effect.effect) === "function") {
                    ef.effect.effect(this, ef.effect, 0);
                }
                else {
                    tgfEffects.effect(ef.effect.effect)(this, ef.effect, 0);
                }
            }
        }
        // Now perform the effects at the current moment of time
        for (i = 0; i < effectNew.length; i += 1) {
            ef = effectNew[i];
            if (typeof (ef.effect.effect) === "function") {
                ef.effect.effect(this, ef.effect, ef.time);
            }
            else {
                tgfEffects.effect(ef.effect.effect)(this, ef.effect, ef.time);
            }
        }
        // Done
        this.effectCurrent = effectNew;
    };
    // ***************************************
    // Preparing, showing and activating
    // ***************************************
    // Prepare the component (and its children) for use
    ComponentBase.prototype.prepare = function (alsoChildren) {
        var _a, _b;
        if (alsoChildren === void 0) { alsoChildren = true; }
        this.baseContainer = this.baseContainer || this.parent.baseContainer;
        if (this.isPrepared) {
            return;
        }
        (_a = this.onPrepare) === null || _a === void 0 ? void 0 : _a.call(this);
        this.isPrepared = true;
        (_b = this.doPrepare) === null || _b === void 0 ? void 0 : _b.call(this);
        if (alsoChildren) {
            for (var i = 0; i < this._components.length; i += 1) {
                this._components[i].prepare();
            }
        }
    };
    // Free the component (and its children) from use
    ComponentBase.prototype.free = function (alsoChildren) {
        var i;
        if (alsoChildren === undefined) {
            alsoChildren = true;
        }
        if (!this.isPrepared) {
            return;
        }
        if (this.onFree) {
            this.onFree();
        }
        if (this.isVisible) {
            this.hide();
        }
        this.isPrepared = false;
        if (alsoChildren) {
            for (i = 0; i < this._components.length; i += 1) {
                this._components[i].free();
            }
        }
        if (this.doFree) {
            this.doFree();
        }
    };
    // Show the component (and its children)
    ComponentBase.prototype.show = function (alsoChildren) {
        var _a, _b;
        if (alsoChildren === void 0) { alsoChildren = true; }
        if (this.isVisible) {
            return;
        }
        if (!this.isPrepared) {
            this.prepare();
        }
        (_a = this.onShow) === null || _a === void 0 ? void 0 : _a.call(this);
        this.isVisible = true;
        this._startEffects();
        (_b = this.doShow) === null || _b === void 0 ? void 0 : _b.call(this);
        if (alsoChildren) {
            for (var i = 0; i < this._components.length; i += 1) {
                this._components[i].show();
            }
        }
    };
    // Hide the component (and its children)
    ComponentBase.prototype.hide = function (alsoChildren) {
        var _a, _b;
        if (alsoChildren === void 0) { alsoChildren = true; }
        if (!this.isVisible) {
            return;
        }
        if (this.isActive) {
            this.deactivate();
        }
        (_a = this.onHide) === null || _a === void 0 ? void 0 : _a.call(this);
        this.isVisible = false;
        if (alsoChildren) {
            for (var i = 0; i < this._components.length; i += 1) {
                this._components[i].hide();
            }
        }
        (_b = this.doHide) === null || _b === void 0 ? void 0 : _b.call(this);
    };
    // Activate the component (and its children)
    ComponentBase.prototype.activate = function (alsoChildren) {
        var _a, _b;
        if (alsoChildren === void 0) { alsoChildren = true; }
        if (this.isActive) {
            return;
        }
        (_a = this.onActivate) === null || _a === void 0 ? void 0 : _a.call(this);
        if (!this.isVisible) {
            this.show();
        }
        this.isActive = true;
        if (alsoChildren) {
            (_b = this.doActivate) === null || _b === void 0 ? void 0 : _b.call(this);
            for (var i = 0; i < this._components.length; i += 1) {
                this._components[i].activate();
            }
        }
    };
    // Deactivate the component (and its children)
    ComponentBase.prototype.deactivate = function (alsoChildren) {
        var _a, _b;
        if (alsoChildren === void 0) { alsoChildren = true; }
        if (!this.isActive) {
            return;
        }
        (_a = this.onDeactivate) === null || _a === void 0 ? void 0 : _a.call(this);
        this.isActive = false;
        if (alsoChildren) {
            for (var i = 0; i < this._components.length; i += 1) {
                this._components[i].deactivate();
            }
        }
        (_b = this.doDeactivate) === null || _b === void 0 ? void 0 : _b.call(this);
    };
    // ***************************************
    // Methods called by parent component when events happen
    // ***************************************
    // Called at the beginning of each step of the dialog that contains the component
    ComponentBase.prototype.handleBeginStep = function (timePassed) {
        if (!this.isActive || !this.isVisible) {
            return;
        }
        // Deal with the effect
        if (this.pauseEffectsOnDeactivate) {
            this._updateEffects(timePassed);
        }
        // Handle the sub-components
        for (var i = 0; i < this._components.length; i += 1) {
            if (this._components[i].handleBeginStep(timePassed)) {
                return true;
            }
        }
    };
    // Called when a touch is started or if a mouse button is pressed
    ComponentBase.prototype.handleTouchDown = function (finger, x, y) {
        if (this.isActive && this.isVisible) {
            // Handle the sub-components
            for (var i = this._components.length - 1; i >= 0; i -= 1) {
                if (this._components[i].handleTouchDown(finger, x, y)) {
                    return true;
                }
            }
            // Handle the component itself
            var xx = this.screenToComponentX(x, y);
            var yy = this.screenToComponentY(x, y);
            if (this.onTouchDown) {
                this.onTouchDown(this, finger, xx, yy);
            }
            if (this.doTouchDown) {
                if (this.doTouchDown(finger, xx, yy)) {
                    return true;
                }
            }
        }
    };
    // Called when a touch is ended or if a mouse button is released
    ComponentBase.prototype.handleTouchUp = function (finger, x, y) {
        if (this.isActive && this.isVisible) {
            // Handle the sub-components
            for (var i = this._components.length - 1; i >= 0; i -= 1) {
                if (this._components[i].handleTouchUp(finger, x, y)) {
                    return true;
                }
            }
            // Handle the component itself
            var xx = this.screenToComponentX(x, y);
            var yy = this.screenToComponentY(x, y);
            if (this.onTouchUp) {
                this.onTouchUp(this, finger, xx, yy);
            }
            if (this.doTouchUp) {
                if (this.doTouchUp(finger, xx, yy)) {
                    return true;
                }
            }
        }
    };
    // Called when the position of the mouse wheel is changed
    ComponentBase.prototype.handleMouseWheel = function (delta, x, y) {
        if (this.isActive && this.isVisible) {
            // Handle the sub-components
            for (var i = this._components.length - 1; i >= 0; i -= 1) {
                if (this._components[i].handleMouseWheel(delta, x, y)) {
                    return true;
                }
            }
            // Handle the component itself
            var xx = this.screenToComponentX(x, y);
            var yy = this.screenToComponentY(x, y);
            if (this.onMouseWheel) {
                this.onMouseWheel(this, delta, xx, yy);
            }
            if (this.doMouseWheel) {
                if (this.doMouseWheel(delta, xx, yy)) {
                    return true;
                }
            }
        }
    };
    // Called immediately when a touch is started or if a mouse button is pressed
    ComponentBase.prototype.handleImmediateTouchDown = function (finger, x, y) {
        if (this.isActive && this.isVisible) {
            // Handle the sub-components
            for (var i = this._components.length - 1; i >= 0; i -= 1) {
                if (this._components[i].handleImmediateTouchDown(finger, x, y)) {
                    return true;
                }
            }
            // Handle the component itself
            var xx = this.screenToComponentX(x, y);
            var yy = this.screenToComponentY(x, y);
            if (this.onImmediateTouchDown) {
                this.onImmediateTouchDown(this, finger, xx, yy);
            }
            if (this.doImmediateTouchDown) {
                if (this.doImmediateTouchDown(finger, xx, yy)) {
                    return true;
                }
            }
        }
    };
    // Called immediately when a touch is ended or if a mouse button is released
    ComponentBase.prototype.handleImmediateTouchUp = function (finger, x, y) {
        if (this.isActive && this.isVisible) {
            // Handle the sub-components
            for (var i = this._components.length - 1; i >= 0; i -= 1) {
                if (this._components[i].handleImmediateTouchUp(finger, x, y)) {
                    return true;
                }
            }
            // Handle the component itself
            var xx = this.screenToComponentX(x, y);
            var yy = this.screenToComponentY(x, y);
            if (this.onImmediateTouchUp) {
                this.onImmediateTouchUp(this, finger, xx, yy);
            }
            if (this.doImmediateTouchUp) {
                if (this.doImmediateTouchUp(finger, xx, yy)) {
                    return true;
                }
            }
        }
    };
    // Called if the key with the given keyCode is pressed
    ComponentBase.prototype.handleKeyDown = function (keyCode) {
        if (this.isActive && this.isVisible) {
            // Handle the component itself
            if (this.onKeyDown) {
                this.onKeyDown(this, keyCode);
            }
            if (this.doKeyDown) {
                if (this.doKeyDown(keyCode)) {
                    return true;
                }
            }
            // Handle the sub-components
            for (var i = 0; i < this._components.length; i += 1) {
                if (this._components[i].handleKeyDown(keyCode)) {
                    return true;
                }
            }
        }
    };
    // Called if the key with the given keyCode is released
    ComponentBase.prototype.handleKeyUp = function (keyCode) {
        if (this.isActive && this.isVisible) {
            // Handle the component itself
            if (this.onKeyUp) {
                this.onKeyUp(this, keyCode);
            }
            if (this.doKeyUp) {
                if (this.doKeyUp(keyCode)) {
                    return true;
                }
            }
            // Handle the sub-components
            for (var i = 0; i < this._components.length; i += 1) {
                if (this._components[i].handleKeyUp(keyCode)) {
                    return true;
                }
            }
        }
    };
    // Called if a global alarm is reached for the dialog that contains the component
    ComponentBase.prototype.handleAlarm = function (alarm) {
        var i;
        if (this.isActive && this.isVisible) {
            // Handle the component itself
            if (this.onAlarm) {
                this.onAlarm(this, alarm);
            }
            if (this.doAlarm) {
                if (this.doAlarm(alarm)) {
                    return true;
                }
            }
            // Handle the sub-components
            for (i = 0; i < this._components.length; i += 1) {
                if (this._components[i].handleAlarm(alarm)) {
                    return true;
                }
            }
        }
    };
    // Called if a special or user-defined event happens for the dialog that contains the component
    ComponentBase.prototype.handleSpecial = function (id, args) {
        var i;
        if (this.isActive && this.isVisible) {
            // Handle the component itself
            if (this.onSpecial) {
                this.onSpecial(this, id, args);
            }
            if (this.doSpecial) {
                if (this.doSpecial(id, args)) {
                    return true;
                }
            }
            // Handle the sub-components
            for (i = 0; i < this._components.length; i += 1) {
                if (this._components[i].handleSpecial(id, args)) {
                    return true;
                }
            }
        }
    };
    // Called at the end of each step of the dialog that contains the component
    ComponentBase.prototype.handleEndStep = function (timePassed) {
        var i;
        if (this.isActive && this.isVisible) {
            // Handle the component itself
            if (this.onEndStep) {
                this.onEndStep(this, timePassed);
            }
            if (this.doEndStep) {
                if (this.doEndStep(timePassed)) {
                    return true;
                }
            }
            // Handle the sub-components
            for (i = 0; i < this._components.length; i += 1) {
                if (this._components[i].handleEndStep(timePassed)) {
                    return true;
                }
            }
        }
    };
    // Called just before drawing (also when not active)
    ComponentBase.prototype.handleUpdateVisuals = function (timePassed) {
        var i;
        if (this.isVisible) {
            // Deal with the effect
            if (!this.pauseEffectsOnDeactivate) {
                this._updateEffects(timePassed);
            }
            this._performEffects();
            // Handle the component itself
            if (this.onUpdateVisuals) {
                this.onUpdateVisuals(this, timePassed);
            }
            if (this.doUpdateVisuals) {
                if (this.doUpdateVisuals(timePassed)) {
                    return true;
                }
            }
            // Handle the sub-components
            for (i = 0; i < this._components.length; i += 1) {
                if (this._components[i].handleUpdateVisuals(timePassed)) {
                    return true;
                }
            }
        }
    };
    // Called when the component must be drawn
    ComponentBase.prototype.handleDraw = function () {
        if (this.isVisible) {
            if (Math.abs(this.xScale) < 0.000001 || Math.abs(this.yScale) < 0.000001) {
                return;
            }
            // Draw the component
            if (this.onDraw) {
                this.onDraw(this);
            }
            this.doDraw();
            // Draw the sub-component
            for (var i = 0; i < this._components.length; i += 1) {
                if (this._components[i].handleDraw()) {
                    return true;
                }
            }
            // End draw the component
            if (this.onEndDraw) {
                this.onEndDraw(this);
            }
            if (this.doEndDraw) {
                if (this.doEndDraw()) {
                    return true;
                }
            }
        }
    };
    return ComponentBase;
}());
export { ComponentBase };
